import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from "react";
import { api, type AuthUser } from "./api";
import { clearAccountPostingDrafts } from "./postingDraftStorage";
import { setApiProfile } from "./profileSession";
import { resetSaved } from "./saved";
import { completePageLeave } from "./pageLeave";
import { clearAvailabilityDraft } from "./availabilityRecovery";
import { isDesktopAdminClient, useDesktopAdminAccess } from "./clientSurface";
import { withAuthFeedbackDelay } from "./authFeedback";

interface AuthState {
  user: AuthUser | null;
  loading: boolean;
  refresh: () => Promise<void>;
  setUser: (u: AuthUser | null) => void;
  logout: () => Promise<void>;
  companyMarketplaceAccess: "personal" | "checking" | "verified" | "unverified" | "history" | "unavailable";
  canStartMarketplaceActivity: boolean;
}
const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [authenticatedUser, updateUser] = useState<AuthUser | null>(null);
  const desktopAdminAccess = useDesktopAdminAccess();
  const user = authenticatedUser?.userType === "admin" && !desktopAdminAccess ? null : authenticatedUser;
  const setUser = useCallback((nextUser: AuthUser | null) => {
    const admittedUser = nextUser?.userType === "admin" && !isDesktopAdminClient() ? null : nextUser;
    if (setApiProfile(admittedUser ? { userId: admittedUser.id, activeContext: admittedUser.activeContext } : null)) resetSaved();
    updateUser(admittedUser);
  }, []);
  useEffect(() => {
    if (authenticatedUser?.userType === "admin" && !desktopAdminAccess) setUser(null);
  }, [authenticatedUser, desktopAdminAccess, setUser]);
  const [loading, setLoading] = useState(true);
  const [companyAccess, setCompanyAccess] = useState<{ user: AuthUser; status: AuthState["companyMarketplaceAccess"] } | null>(null);
  const companyMarketplaceAccess = !user?.activeContext?.startsWith("company:") ? "personal"
    : companyAccess?.user.id === user.id && companyAccess.user.activeContext === user.activeContext ? companyAccess.status : "checking";
  // [business.marketplace-eligibility](../../../docs/product/businesses.md#business-marketplace-eligibility): posting and bookmarks share this gate; unknown or historical
  // Business scopes cannot start activity. Each API write still checks access.
  const canStartMarketplaceActivity = companyMarketplaceAccess === "personal" || companyMarketplaceAccess === "verified";

  // [business.marketplace-eligibility](../../../docs/product/businesses.md#business-marketplace-eligibility): selected-company controls use current business trust, not the
  // member's identity badge. Retain known status during same-profile refresh
  // so posting drafts stay mounted; the API checks every write independently.
  // Ignore a slow reply after a profile switch.
  useEffect(() => {
    if (!user?.activeContext?.startsWith("company:")) return;
    let alive = true;
    void api.getContexts().then((contexts) => {
      if (!alive) return;
      const company = contexts.companies.find((choice) => choice.key === user.activeContext);
      const history = contexts.historyCompanies?.some((choice) => choice.key === user.activeContext);
      setCompanyAccess({ user, status: history ? "history" : company ? company.verified ? "verified" : "unverified" : "unavailable" });
    }).catch(() => { if (alive) setCompanyAccess({ user, status: "unavailable" }); });
    return () => { alive = false; };
  }, [user]);

  const refresh = useCallback(async () => {
    try {
      const me = await api.me();
      // Don't let a slow initial /me (no session) clobber a user just set by login.
      if (me) setUser(me);
    } catch { /* keep */ } finally { setLoading(false); }
  }, [setUser]);
  useEffect(() => { refresh(); }, [refresh]);

  // Email verification often happens in Gmail or another browser. Reconcile
  // the session when the user returns to this app so the verification banner
  // does not remain visible until a full reload.
  useEffect(() => {
    const reconcile = () => { void refresh(); };
    window.addEventListener("focus", reconcile);
    document.addEventListener("visibilitychange", reconcile);
    return () => {
      window.removeEventListener("focus", reconcile);
      document.removeEventListener("visibilitychange", reconcile);
    };
  }, [refresh]);

  const logout = useCallback(async () => {
    // Keep the signed-in tree and its open sign-out surface mounted through feedback.
    try { await withAuthFeedbackDelay(() => api.logout()); } catch {
      // A surviving HttpOnly cookie can restore the account. Never claim sign-out
      // until revocation succeeds; retain the signed-in UI for an explicit retry.
      window.alert("Sign-out did not complete. You are still signed in. Reconnect and try signing out again before leaving this device.");
      throw new Error("Sign-out did not complete.");
    }
    clearAccountPostingDrafts(String(user?.id ?? "guest"));
    clearAvailabilityDraft();
    completePageLeave();
    setUser(null);
    resetSaved();
  }, [user?.id, setUser]);

  return <Ctx.Provider value={{ user, loading, refresh, setUser, logout, companyMarketplaceAccess, canStartMarketplaceActivity }}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const c = useContext(Ctx);
  if (!c) throw new Error("useAuth must be used within AuthProvider");
  return c;
}
